import React, { useState, useRef, useEffect } from "react";
import { BrowserRouter } from "react-router-dom";
import CancelIcon from "@mui/icons-material/Cancel";
import Search from "@mui/icons-material/Search";
/* eslint-disable import/no-unresolved */
import {
  updateStateSubject,
  createStateSubscription,
  destroyStateSubscription,
} from "@gartner/genie-messenger-util";
import { Typography, useDebounce } from "@genie-ui/react";
import { useComponentVisible } from "@hooks";
import {
  HighlighteText,
  ErrorMessage,
  SearchLoader,
  ListGroup,
  OptionItem,
} from "@components";
import { useMediaQuery } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import apiService from "@utils/apiService";
import { verifyApiResponse } from "@utils/helper";

import { Constants } from "@constants";

import "./Autocomplete.scss";

const Autocomplete = (props) => {
  const [isActive, setIsActive] = useState(false);
  const [value, setValue] = useState('');
  const [state, setState] = useState({
    isLoading: false,
    data: null,
    error: null,
  });
  const [showLoader, setShowLoader] = useState(false);
  const debouncedSearchTerm = useDebounce(value, 500);
  const inputref = useRef(null);
  const [displayLastLabel, setDisplayLastLabel] = useState(true);
  const { ref, isComponentVisible, setIsComponentVisible } =
    useComponentVisible(false);

  const {
    width = "100%",
    maxWidth = 540,
    startAdornment = <Search size={16} style={{ color: "#002856" }} />,
    endAdornment = null,
    displayListWithoutSections = false,
    lastOptionLabel = "",
    lastOptionContainerClass = "",
    lastOptionLabelConcat = true,
    placeholder = "",
    roundedBorders = true,
    errorBorderDisplay = false,
    searchCallActis = null,
    searchDropdownLastOptionClick = null,
    onOptionClick = null,
    showInModal = false,
    handleLastOptionClick = null,
    handleEnterKeyPress = null,
    showClear = true,
    isOptionRouteable = true,
    inputAutoFocus = false,
    inputFocus = false,
    setFocus = false,
    mobileSearchData = {},
    defaultValue = "",
    setInputFocus = () => {},
    componentName = "",
    enableBackdropClickClose = true,
    dropdownUpdatedEvent = () => { }
  } = props;

  const isMobile =
    useMediaQuery("(max-width:1023px)") &&
    !displayListWithoutSections &&
    !showInModal;

  useEffect(() => {
    if (isMobile) {
      if (
        mobileSearchData?.active &&
        (setFocus || inputFocus)
      ) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "unset";
      }
    }
  }, [mobileSearchData?.active, setFocus, inputFocus]);

  useEffect(() => {
    if (inputAutoFocus) {
      const input = inputref?.current;
      setTimeout(() => {
        if (input) {
          input.focus();
        }
      }, 100);
    }
  }, []);

  useEffect(() => {
    if (setFocus) {
      onFocus();
      const input = inputref?.current;
      input.focus();
    }
  }, [setFocus]);

  const onChange = (e) => {
    const value = e.target.value;
    setValue(value);
    if (!isActive) {
      setIsActive(true);
      setIsComponentVisible(true);
    }
  };
  const onFocus = () => {
    setIsActive(true);
    setIsComponentVisible(true);
  };
  const onCancelClicked = () => {
    setValue("");
    const input = inputref?.current;
    input.value = "";
    input.focus();
  };

  const displayClearIcon = () => {
    if (value?.length > 0) {
      return (
        <div
          onClick={onCancelClicked}
          className="input-clear-container"
          role="button"
          tabIndex={0}
        >
          <CancelIcon className="input__clear" fontSize="16px" />
        </div>
      );
    }
    return null;
  };

  const handleOptionSelect = (id, name) => {
    setValue(name);
    setIsActive(false);
    setInputFocus(false);
    updateStateSubject("searched_company_name", { org_id: id });
  };

  const renderProductList = (product, i) => {
    return (
      <HighlighteText
        key={product}
        text={`${i === 0 ? "" : ","} ${product}`}
        highlight={value}
      />
    );
  };

  const getOptionLabel = (item) => {
    return (
      <li className="menu__list__item" key={item.org_name}>
        <div
          onClick={() => handleOptionSelect(item?.org_id, item?.org_name)}
          className="menu__list__item-link"
          role="button"
          tabIndex={0}
        >
          <Typography
            variant="body5"
            className="menu__list__item-text menu__list__item-product"
          >
            {item?.product_name?.map(renderProductList)}
            {item?.product_name?.length > 0 && Constants.SEARCH.BY_WORD}
            <Typography
              className="menu__list__item-text  menu__list__item-product"
              variant="body1"
            >
              <HighlighteText text={item?.org_name} highlight={value} />
            </Typography>
          </Typography>
        </div>
      </li>
    );
  };

  const handleOptionClick = (item) => {
    setState({
      ...state,
      data: null,
    });
    if(isOptionRouteable){
      setValue("");
      setIsActive(false);
      setInputFocus(false);
      updateStateSubject("search", { active: false });
    }
    if (onOptionClick) {
      onOptionClick(item);
    }
  };

  const getOptionItem = (item) => {
    const userInput = value?.toLowerCase().trim();
    if (
      userInput === item?.vendorName?.toLowerCase() ||
      userInput === item?.profileName?.toLowerCase() ||
      userInput === item?.productName?.toLowerCase()
    ) {
      setDisplayLastLabel(false);
    }
    if (displayListWithoutSections) {
      if (
        userInput === item?.org_name?.toLowerCase() ||
        item?.product_name?.some(
          (productName) => productName?.toLowerCase() === userInput
        )
      ) {
        setDisplayLastLabel(false);
      }
      return getOptionLabel(item);
    }
    if (item?.vendorName && item?.profileName) {
      return (
        <OptionItem
          optionKey={item?.profileName}
          optionLink={isOptionRouteable ? `/risk-profile?profileId=${item.profileId}` : null}
          vendorName={item?.vendorName}
          productOrProfileName={item?.profileName}
          type={item?.type}
          optionValue={value}
          isOptionRouteable={isOptionRouteable}
          handleOptionClick={() => handleOptionClick(item)}
        />
      );
    } else if (item.vendorName) {
      const seoName =
        item?.type === Constants.SEARCH.PRODUCT_TYPE
          ? item?.productSEOName
          : item?.vendorSEOName;
      const optionLink = item.profileId
        ? `/risk-profile?profileId=${item.profileId}`
        : `/risk-profile?profileId=${seoName}&type=${item?.type}&shell=true`;
      return (
        <OptionItem
          optionKey={item.vendorId}
          optionLink={isOptionRouteable ? optionLink : null}
          vendorName={item?.vendorName}
          productOrProfileName={item?.productName}
          type={item?.type}
          optionValue={value}
          isOptionRouteable={isOptionRouteable}
          handleOptionClick={() => handleOptionClick(item)}
        />
      );
    }
    return null;
  };

  useEffect(() => {
    enableBackdropClickClose && setIsActive(isComponentVisible);
  }, [isComponentVisible]);
  useEffect(() => {
    if (debouncedSearchTerm.length >= 2) {
      setState({
        ...state,
        isLoading: true,
        error: null,
      });
      setShowLoader(true);
      const searchedTerm = encodeURIComponent(value);
      setDisplayLastLabel(true);
      const URL = displayListWithoutSections
        ? `${Constants.API.GCMS_URL}${searchedTerm}${Constants.API.GCMS_SEARCH_PARAMS}`
        : `${Constants.API.BASE_URL}${Constants.API.SEARCH}${Constants.API.SEARCH_PARAMS}${searchedTerm}`;
      apiService
        .get(`${URL}`)
        .then((data) => {
          let searchData = verifyApiResponse(data);
          setState({
            ...state,
            isLoading: false,
            error: null,
            data: displayListWithoutSections
              ? [...searchData?.data]
              : { ...searchData?.data } || {
                  inOrg: [],
                  outsideOrg: [],
                },
          });
          if (searchCallActis != null) {
            searchCallActis({
              searchTerm: value,
              inMyOrganization:
                searchData?.data?.inOrg?.length > 0
                  ? Constants.SEARCH.FOUND
                  : Constants.SEARCH.NOT_FOUND,
              outsideMyOrganization:
                searchData?.data?.outsideOrg?.length > 0
                  ? Constants.SEARCH.FOUND
                  : Constants.SEARCH.NOT_FOUND,
            });
          }
          setShowLoader(false);
          searchData?.data && dropdownUpdatedEvent(searchData?.data, false)
        })
        .catch(() => {
          setState({
            ...state,
            isLoading: false,
            error: true,
          });
          setShowLoader(false);
          dropdownUpdatedEvent({}, true)
        });
    }
  }, [debouncedSearchTerm]);

  const handleInputFocus = () => {
    setInputFocus(true);
    updateStateSubject("searched_company_name", { active: true });
    if (isMobile) {
      updateStateSubject("search", { active: true });
    }
  };

  const handleLastOption = () => {
    if (handleLastOptionClick != null) {
      handleLastOptionClick(value);
      setValue(value);
      setIsActive(false);
    } else {
      if (searchDropdownLastOptionClick != null) {
        searchDropdownLastOptionClick({ searchTerm: value });
      }
      if (displayListWithoutSections) {
        setValue(value);
        setIsActive(false);
        updateStateSubject("searched_company_name", { org_name: value });
      } else {
        setValue("");
        setInputFocus(false);
        updateStateSubject("search", { active: false });
        updateStateSubject("send_risk_assessment", {
          existingVendor: false,
          vendorName: value?.trim(),
          componentName,
        });
      }
    }
  };

  useEffect(() => {
    setValue(defaultValue || "");
  }, [defaultValue]);

  return (
    <div
      name="autocomplete"
      style={{ maxWidth, width }}
      ref={ref}
      className="genie-autocomplete"
      onFocus={onFocus}
    >
      <div
        className={`${
          roundedBorders ? "input-root__rounded-border" : ""
        } input-root input-border ${
          errorBorderDisplay ? "input-border-error" : ""
        }`}
      >
        {!isActive && value.length <= 0 && startAdornment}
        <input
          className={`input-base ${
            isMobile && mobileSearchData?.active
              ? "input-base--active"
              : ""
          }`}
          onChange={onChange}
          onKeyDown={(e) => {
            if (e.key === "Enter" && value?.length >= 2) {
              e.preventDefault();
              if (showInModal) {
                setIsActive(false);
                handleEnterKeyPress && handleEnterKeyPress(value);
              }
            }
          }}
          type={"text"}
          ref={inputref}
          value={value}
          placeholder={placeholder}
          onFocus={handleInputFocus}
        />
        {showLoader && <SearchLoader />}
        {showClear && displayClearIcon()}
        {endAdornment}
      </div>
      {isMobile && isActive && (value?.length < 2 || setFocus) && (
        <div
          className={`menu-base ${setFocus ? "menu-base__mobile" : ""}`}
        >
          <Typography variant="body3">
            {Constants.SEARCH.SEARCH_START_TYPING_TEXT}
          </Typography>
        </div>
      )}
      {isActive && value?.length >= 2 && (
        <div
          className={`menu-base ${setFocus ? "menu-base__mobile" : ""}`}
        >
          {!state?.isLoading && state?.error && (
            <ul className="menu__list-root menu__list-root--error">
              <ErrorMessage />
            </ul>
          )}
          {!state?.isLoading && !state?.error && state?.data && (
            <ul className="menu__list-root">
              {displayListWithoutSections ? (
                <ListGroup orgData={state.data} getOptionItem={getOptionItem} />
              ) : (
                <>
                  <ListGroup
                    orgData={state.data.inOrg}
                    title={Constants.SEARCH.IN_MY_ORG_TITLE}
                    noMatchesLabel={Constants.SEARCH.NO_RESULT_IN_MY_ORG_TITLE}
                    getOptionItem={getOptionItem}
                  />
                  <ListGroup
                    orgData={state.data.outsideOrg}
                    title={Constants.SEARCH.OUTSIDE_ORG_TITLE}
                    noMatchesLabel={
                      Constants.SEARCH.NO_RESULT_OUTSIDE_ORG_TITLE
                    }
                    getOptionItem={getOptionItem}
                  />
                </>
              )}
              {displayLastLabel && (
                <li
                  className={`menu__list-group menu__list-group-sra ${lastOptionContainerClass}`}
                >
                  <ul className="menu__list__item-root">
                    <li
                      className="menu__list__item menu__list__item-sra"
                      key={value}
                      onClick={handleLastOption}
                      role="presentation"
                    >
                      <Typography className="menu__list__item-product">
                        {lastOptionLabel}
                        {lastOptionLabelConcat && (
                          <>
                            <span className="menu__list__item-sra__text">{`${value.trim()}`}</span>
                            &quot;
                          </>
                        )}
                      </Typography>
                    </li>
                  </ul>
                </li>
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

const AutocompleteWrapper = (props) => {
  const isMobile = useMediaQuery("(max-width:1023px)") && !props?.showInModal;
  const [backState, setBackState] = useState(false);
  const [inputAreaFocus, setInputAreaFocus] = useState(false);
  const [searchData, setSearchData] = useState({});
  const baseName = props.displayListWithoutSections
    ? ""
    : Constants.ROUTES.BASE_URL;
  const { setFocus = false } = props;
  useEffect(() => {
    const subscriptionSearchDetails = createStateSubscription(
      "search",
      (data) => {
        setSearchData(data);
      }
    );

    return () => {
      destroyStateSubscription(subscriptionSearchDetails);
    };
  }, []);

  useEffect(() => {
    if (!searchData?.active) {
      setBackState(false);
    }
  }, [searchData?.active]);

  const handleBackClick = () => {
    setInputAreaFocus(false);
    setBackState(true);
    updateStateSubject("search", { active: false });
  };
  if (backState && (setFocus || inputAreaFocus)) {
    return null;
  }
  if (
    isMobile &&
    !props.displayListWithoutSections &&
    (setFocus || inputAreaFocus)
  ) {
    return (
      <div className="genie-autocomplete__mobile">
        <ArrowBackIcon
          className="genie-autocomplete__mobile__back"
          fontSize="24px"
          onClick={handleBackClick}
        />
        <div className="genie-autocomplete__mobile__search">
          <BrowserRouter basename={baseName}>
            <Autocomplete
              {...props}
              setFocus={searchData?.active}
              inputFocus={inputAreaFocus}
              setInputFocus={setInputAreaFocus}
              mobileSearchData={searchData}
            />
          </BrowserRouter>
        </div>
      </div>
    );
  }
  return (
    <BrowserRouter basename={baseName}>
      <Autocomplete
        {...props}
        inputFocus={inputAreaFocus}
        setInputFocus={setInputAreaFocus}
        mobileSearchData={searchData}
      />
    </BrowserRouter>
  );
};

export default AutocompleteWrapper;
